import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Textarea,
} from '@rallyshift/design-system'
import api, { PlatformOrganizationDetail } from '../../api/client'
import { ROOT_DOMAIN, tenantUrl } from '../../api/tenant'
import { ORG_STATUSES, PLANS, SUB_STATUSES } from '../../utils/subscription'

function dateInput(value: string | null): string {
  if (!value) return ''
  return value.slice(0, 10)
}

function isoDay(value: string): string | null {
  return value ? new Date(`${value}T00:00:00`).toISOString() : null
}

const PlatformOrgPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [org, setOrg] = useState<PlatformOrganizationDetail | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState<'json' | 'zip' | null>(null)

  const [plan, setPlan] = useState('trial')
  const [subscriptionStatus, setSubscriptionStatus] = useState('trialing')
  const [seatLimit, setSeatLimit] = useState('')
  const [trialEndsAt, setTrialEndsAt] = useState('')
  const [startedAt, setStartedAt] = useState('')
  const [currentPeriodStart, setCurrentPeriodStart] = useState('')
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState('active')

  const [domain, setDomain] = useState('')
  const [savingDomain, setSavingDomain] = useState(false)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmSlug, setConfirmSlug] = useState('')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!id) return
    api.platform
      .getOrganization(id)
      .then((loaded) => {
        setOrg(loaded)
        setPlan(loaded.plan)
        setSubscriptionStatus(loaded.subscription_status)
        setSeatLimit(loaded.seat_limit == null ? '' : String(loaded.seat_limit))
        setTrialEndsAt(dateInput(loaded.trial_ends_at))
        setStartedAt(dateInput(loaded.subscription_started_at))
        setCurrentPeriodStart(dateInput(loaded.current_period_start))
        setCurrentPeriodEnd(dateInput(loaded.current_period_end))
        setNotes(loaded.subscription_notes || '')
        setStatus(loaded.status)
        setDomain(loaded.custom_domain || '')
      })
      .catch((e: Error) => setError(e.message || 'Organisatie kon niet worden geladen.'))
  }, [id])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!org) return
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const updated = await api.platform.updateSubscription(org.id, {
        plan,
        subscription_status: subscriptionStatus,
        seat_limit: seatLimit.trim() === '' ? null : Number(seatLimit),
        trial_ends_at: isoDay(trialEndsAt),
        subscription_started_at: isoDay(startedAt),
        current_period_start: isoDay(currentPeriodStart),
        current_period_end: isoDay(currentPeriodEnd),
        subscription_notes: notes.trim() || null,
        status,
      })
      setOrg(updated)
      setSuccess('Abonnement opgeslagen.')
    } catch (err: any) {
      setError(err.message || 'Opslaan is niet gelukt.')
    } finally {
      setSaving(false)
    }
  }

  const saveDomain = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!org) return
    setSavingDomain(true)
    setError('')
    setSuccess('')
    try {
      const updated = await api.platform.updateCustomDomain(org.id, domain.trim() || null)
      setOrg(updated)
      setDomain(updated.custom_domain || '')
      setSuccess(updated.custom_domain ? `Gekoppeld aan ${updated.custom_domain}.` : 'Eigen domein verwijderd.')
    } catch (err: any) {
      setError(err.message || 'Opslaan is niet gelukt.')
    } finally {
      setSavingDomain(false)
    }
  }

  const doExport = async (format: 'json' | 'zip') => {
    if (!org) return
    setExporting(format)
    setError('')
    try {
      if (format === 'json') await api.platform.exportJson(org.id, org.slug)
      else await api.platform.exportZip(org.id, org.slug)
    } catch (err: any) {
      setError(err.message || 'Export is niet gelukt.')
    } finally {
      setExporting(null)
    }
  }

  const remove = async () => {
    if (!org) return
    setDeleting(true)
    setError('')
    try {
      await api.platform.deleteOrganization(org.id, confirmSlug)
      navigate('/platform', { replace: true })
    } catch (err: any) {
      setError(err.message || 'Verwijderen is niet gelukt.')
      setConfirmOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  if (!org && !error) {
    return <p className="text-md text-text-secondary">Laden…</p>
  }

  if (!org) {
    return (
      <Alert variant="danger">{error}</Alert>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-display-md text-text-primary">{org.name}</h1>
          <p className="mt-1 text-md text-text-secondary">
            {org.slug}.{ROOT_DOMAIN}
            {org.custom_domain ? ` · ${org.custom_domain}` : ''}
            {org.contact_email ? ` · ${org.contact_email}` : ''}
          </p>
        </div>
        <Button onClick={() => window.location.assign(tenantUrl(org.slug, '/dashboard'))}>
          Openen
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onDismiss={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card title="Abonnement" meta="Alleen een registratie. Dit sluit de organisatie niet af.">
        <form onSubmit={save} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Select label="Plan" value={plan} onChange={(e) => setPlan(e.target.value)} options={PLANS} />
            <Select
              label="Abonnementsstatus"
              value={subscriptionStatus}
              onChange={(e) => setSubscriptionStatus(e.target.value)}
              options={SUB_STATUSES}
            />
            <Input
              label="Zitplaatsen"
              type="number"
              min={1}
              value={seatLimit}
              onChange={(e) => setSeatLimit(e.target.value)}
              hint="Leeg = onbeperkt."
            />
            <Select
              label="Organisatiestatus"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={ORG_STATUSES}
            />
            <Input
              label="Einde proef"
              type="date"
              value={trialEndsAt}
              onChange={(e) => setTrialEndsAt(e.target.value)}
            />
            <Input
              label="Klant sinds"
              type="date"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              hint="Eerste dag van het betaalde abonnement."
            />
            <Input
              label="Begin periode"
              type="date"
              value={currentPeriodStart}
              onChange={(e) => setCurrentPeriodStart(e.target.value)}
            />
            <Input
              label="Einde periode"
              type="date"
              value={currentPeriodEnd}
              onChange={(e) => setCurrentPeriodEnd(e.target.value)}
            />
          </div>
          <Textarea
            label="Notities"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
          <Button type="submit" loading={saving}>
            Opslaan
          </Button>
        </form>
      </Card>

      <Card
        title="Eigen domein"
        meta={`Laat de klant een CNAME naar ${org.slug}.${ROOT_DOMAIN} aanmaken en voeg het domein toe in Coolify voor het certificaat.`}
      >
        <form onSubmit={saveDomain} className="space-y-4">
          <Input
            label="Domein"
            placeholder="vrijwilligers.voorbeeld.nl"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            hint="Leeg = alleen het webadres. E-mails linken naar het eigen domein zodra het is ingesteld."
          />
          <Button type="submit" loading={savingDomain}>
            Domein opslaan
          </Button>
        </form>
      </Card>

      <Card title="Export" meta="JSON kun je later weer importeren. CSV is om in een spreadsheet te openen.">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={exporting === 'json'}
            disabled={!!exporting}
            onClick={() => doExport('json')}
          >
            JSON downloaden
          </Button>
          <Button
            variant="secondary"
            loading={exporting === 'zip'}
            disabled={!!exporting}
            onClick={() => doExport('zip')}
          >
            CSV-zip downloaden
          </Button>
        </div>
      </Card>

      <Card
        title="Verwijderen"
        meta="Verwijdert de organisatie en alles wat eraan hangt. Accounts blijven bestaan."
      >
        <Button variant="danger" onClick={() => setConfirmOpen(true)}>
          Organisatie verwijderen
        </Button>
      </Card>

      <Modal
        open={confirmOpen}
        danger
        title={`Verwijder ${org.name}?`}
        onClose={() => setConfirmOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Annuleren
            </Button>
            <Button
              variant="danger"
              loading={deleting}
              disabled={confirmSlug.trim().toLowerCase() !== org.slug}
              onClick={remove}
            >
              Definitief verwijderen
            </Button>
          </div>
        }
      >
        <p className="mb-4 text-md text-text-secondary">
          Typ <span className="font-semibold text-text-primary">{org.slug}</span> om te bevestigen.
          Dit kan niet ongedaan worden gemaakt.
        </p>
        <Input
          label="Webadres"
          value={confirmSlug}
          onChange={(e) => setConfirmSlug(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
      </Modal>
    </div>
  )
}

export default PlatformOrgPage
